import React from 'react';
import { Link } from 'react-router-dom';
import { useGuidesBase } from '../../hooks/useGuidesBase';
import { Box, Title, Paper, Text, Stack, Anchor, Alert, Breadcrumbs, List, Table } from '@mantine/core';
import { Lightbulb } from '@phosphor-icons/react';
import SEO from '../../components/SEO';

export default function MetaAudienceGuide() {
  const guidesBase = useGuidesBase();
  return (
    <Box px="lg" py="md">
      <SEO
        title="Targetowanie odbiorców w Meta Ads"
        description="Zainteresowania, zachowania, dane demograficzne, Lookalike Audiences. Jak tworzyć skuteczne grupy docelowe na Facebooku."
        path={`${guidesBase}/meta-audience`}
        noIndex={guidesBase !== '/poradniki'}
      />
      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Targetowanie odbiorców</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">Targetowanie odbiorców w Meta Ads</Title>

      <Alert color="blue" mb="md" icon={<Lightbulb size={16} />}>
        <Text size="sm">W odróżnieniu od Google Ads (gdzie docierasz do osób szukających konkretnych fraz), Meta Ads pozwala docierać do osób na podstawie ich zainteresowań, zachowań i danych demograficznych.</Text>
      </Alert>

      <Stack gap="md">
        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">Typy targetowania</Text>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Typ</Table.Th>
                <Table.Th>Opis</Table.Th>
                <Table.Th>Przykład</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Zainteresowania</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">Katalog zainteresowań Facebooka</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">Ubezpieczenia, Nieruchomości, Fitness</Text></Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Demografia</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">Wiek, płeć, lokalizacja</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">25-55 lat, Polska, kobiety</Text></Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Zachowania</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">Aktywność online, zakupy</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">Zakupy online, aktywni mobilnie</Text></Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Lookalike</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">Osoby podobne do Twoich klientów</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">1-3% Lookalike z bazy kupujących</Text></Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">Jak Droid dobiera grupę docelową</Text>
          <List size="sm" spacing="xs">
            <List.Item>Agent AI analizuje Twoją stronę internetową i określa profil klienta</List.Item>
            <List.Item>Wyszukuje zainteresowania w katalogu Facebooka pasujące do Twojego biznesu</List.Item>
            <List.Item>Proponuje wiek, płeć i lokalizację na podstawie typu działalności</List.Item>
            <List.Item>Możesz ręcznie dodawać i usuwać zainteresowania w kreatorze kampanii</List.Item>
            <List.Item>Szacowany zasięg aktualizuje się w czasie rzeczywistym</List.Item>
          </List>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">Wskazówki</Text>
          <List size="sm" spacing="xs">
            <List.Item>Nie zawężaj zbytnio grupy docelowej. Algorytm Meta lepiej optymalizuje przy większych grupach (100K+).</List.Item>
            <List.Item>Używaj 5-10 zainteresowań. Za mało ogranicza zasięg, za dużo rozmywa targeting.</List.Item>
            <List.Item>Testuj różne grupy wiekowe. Często najlepsze wyniki daje szeroki zakres (25-55).</List.Item>
            <List.Item>Lookalike Audiences działają najlepiej gdy masz przynajmniej 1000 klientów w bazie źródłowej.</List.Item>
          </List>
        </Paper>
      </Stack>
    </Box>
  );
}
